/**
 * SignupFlow — multi-phase signup: form → verify → presets → done.
 * Age logic: computeAge only on full date; email required ≥13; kids checkbox ≥18.
 * Child creation is INLINE in the form (no separate phase).
 */
import { useEffect, useRef, useState } from 'react';
import { API_BASE, adoptNativeRefreshToken, clientHeaders, credentialsMode, nativeAuthFields, setAccessToken, getAccessToken, refreshAccessToken, request, demo, device, observability, type VerificationStatusResponse } from '../api';
import { ChromeTokens } from '../chrome/tokens';
import { Icon } from '../chrome/Icon';
import { NameField, type AvailabilityStatus } from '../chrome/NameField';
import { TrustDeviceToggle } from '../chrome/TrustDeviceToggle';
import { InfoTip } from '../chrome/InfoTip';
import { PresetLadder } from '../privacy/PresetLadder';
import { LegalModal } from '../legal/LegalModal';
import { acceptTerms as acceptTermsApi } from '../lifecycle/api';
// Public-shareable-link signup-intent threading (R3). Additive only:
// useIntent reads URL + sessionStorage; IntentRibbon renders ABOVE the
// existing form; the signed-in shell resolves it after (useResolveIntentOnMount,
// U10). The form itself is unchanged — every age-conditional path,
// validation, child step, consent gate, and trust-device behavior
// stays exactly as today when there's no intent in the URL.
import { useIntent, readPersistedIntent } from '../share/useIntent';
import { IntentRibbon } from '../share/IntentRibbon';
import { webOrigin } from '../native/webOrigin';
import { computeAge, isGrownUp, needsEmailAndTerms } from './ageRules';
import { checkUsernamePublic } from './checkUsername';

interface SignupFlowProps {
  onComplete: () => void;
  onBackToLogin?: () => void;
}


// A child is ONE live card — the card *is* the child, no hidden "draft" to
// commit, so nothing the parent typed can silently vanish.
// NOTE: no `email` field. COPPA verifiable parental consent (VPC) cannot exist
// at signup — the parent account itself doesn't exist yet. Email on a child can
// only be set later, from Parental Controls, after the parent clears VPC.
export interface ChildForm {
  key: string;
  name: string;
  password: string;
  birthdate: string;
  // Live username availability for THIS card's name (driven by NameField).
  availability: AvailabilityStatus;
}
let childKeySeq = 0;
const blankChild = (): ChildForm => ({
  key: `c${++childKeySeq}`,
  name: '',
  password: '',
  birthdate: '',
  availability: 'idle',
});
// 'ready' requires name + birthday AND a username that is available and not
// colliding with the parent or a sibling. 'checking'/'taken'/'collision' keep a
// card out of 'ready' so the header badge can never say "Ready" while the
// username row shows a problem (Req 3.5).
export type ChildStatus =
  | 'empty'
  | 'incomplete'
  | 'checking'
  | 'taken'
  | 'collision'
  | 'ready';
export const childStatus = (c: ChildForm, colliding: boolean): ChildStatus => {
  const hasName = !!c.name.trim();
  const hasDob = !!c.birthdate;
  if (!hasName && !hasDob && !c.password) return 'empty';
  if (!hasName || !hasDob) return 'incomplete';
  if (colliding) return 'collision';
  if (c.availability === 'taken' || c.availability === 'invalid') return 'taken';
  if (c.availability === 'available') return 'ready';
  // name + birthday present but availability not yet resolved (idle/checking/
  // unchanged) — treat as still checking, never ready.
  return 'checking';
};

// In-batch collisions (Req 2): names that aren't yet persisted, so the backend
// availability check can't catch them. A child collides if its trimmed,
// lowercased name equals the parent's chosen username or another child's name.
// Returns the set of colliding child keys.
export function computeCollisions(parentUsername: string, children: ChildForm[]): Set<string> {
  const colliding = new Set<string>();
  const parent = parentUsername.trim().toLowerCase();
  const byName = new Map<string, string[]>();
  for (const c of children) {
    const n = c.name.trim().toLowerCase();
    if (!n) continue;
    if (n === parent) colliding.add(c.key);
    byName.set(n, [...(byName.get(n) ?? []), c.key]);
  }
  for (const keys of byName.values()) {
    if (keys.length > 1) keys.forEach((k) => colliding.add(k));
  }
  return colliding;
}

// Decode the `sub` (user id) claim from a JWT without verifying it — used only
// to attach the parent's id to a client-emitted observability event.
function decodeJwtSub(token: string | null | undefined): string | undefined {
  if (!token) return undefined;
  try {
    const payload = JSON.parse(atob(token.split('.')[1] ?? ''));
    return typeof payload.sub === 'string' ? payload.sub : undefined;
  } catch {
    return undefined;
  }
}


export function SignupFlow({ onComplete, onBackToLogin }: SignupFlowProps) {
  // Public-shareable-link intent state. `intent` is null for every
  // organically-initiated signup (no URL params, no sessionStorage),
  // and IntentRibbon renders nothing in that case — the existing
  // form layout is byte-identical to today.
  const intentState = useIntent();
  // The intent is resolved by the signed-in shell once it is up (game-on-freeform U10,
  // useResolveIntentOnMount): there its billing and consent sheets exist, and a sign-in
  // resolves it the same way. The form only carries it to /auth/register.
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [birthdate, setBirthdate] = useState('');
  const [email, setEmail] = useState('');
  const [trust, setTrust] = useState(true);
  const [wantsKids, setWantsKids] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<'form' | 'verify' | 'presets' | 'done'>('form');

  // Inline children — each card IS a child (no hidden draft). Empty cards are
  // ignored at submit; incomplete ones (some info, missing name/birthday) block
  // submit and surface inline errors, but every card is always removable.
  const [childForms, setChildForms] = useState<ChildForm[]>([]);
  const [triedSubmit, setTriedSubmit] = useState(false);

  // Post-submit state
  const [verificationUrl, setVerificationUrl] = useState('');
  const [verificationSessionId, setVerificationSessionId] = useState('');
  const [createdChildIds, setCreatedChildIds] = useState<{ id: string; username: string }[]>([]);
  const [presetIndex, setPresetIndex] = useState(0);
  const [kidPreset, setKidPreset] = useState('moderated');
  // Any preset but Solo needs the grown-up check (PUT /privacy/child/{id}/preset
  // answers 403 parent_verification_required). A parent who skipped it starts
  // on Solo and is told why; Moderated is there after the check.
  const [parentVerified, setParentVerified] = useState(true);
  const toPresets = (verified: boolean) => {
    setParentVerified(verified);
    setKidPreset(verified ? 'moderated' : 'solo');
    setPhase('presets');
  };

  const age = computeAge(birthdate);
  const isAdult = isGrownUp(age);
  const emailRequired = needsEmailAndTerms(age);
  // launch-ux-followups Item 5 + launch-compliance R5: ages 13+ must
  // tick a Terms checkbox; under-13 sees an informational note (the
  // binding acceptance happens later at Consent Gate). The checkbox
  // only renders once a birthdate is present.
  const termsRequired = needsEmailAndTerms(age);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [legalDoc, setLegalDoc] = useState<"privacy" | "terms" | null>(null);
  const canSubmit =
    username.length >= 3 && !!password && !!birthdate
    && (!emailRequired || !!email)
    && (!termsRequired || acceptedTerms)
    && !loading;

  const realChildren = childForms.filter((c) => childStatus(c, false) !== 'empty');
  const collisions = computeCollisions(username, childForms);
  const statusOf = (c: ChildForm): ChildStatus => childStatus(c, collisions.has(c.key));
  const readyChildren = childForms.filter((c) => statusOf(c) === 'ready');
  // A real child that isn't ready blocks submit (incomplete, still checking,
  // taken, or colliding) — extends the old "incomplete" gate (Req 3).
  const hasBlockingChild = realChildren.some((c) => statusOf(c) !== 'ready');

  const toggleKids = (on: boolean) => {
    setWantsKids(on);
    if (on && childForms.length === 0) setChildForms([blankChild()]);
    if (!on) setTriedSubmit(false);
  };
  const updateChild = (key: string, patch: Partial<ChildForm>) =>
    setChildForms((cs) => cs.map((c) => (c.key === key ? { ...c, ...patch } : c)));
  const addChild = () => setChildForms((cs) => [...cs, blankChild()]);
  const removeChild = (key: string) =>
    setChildForms((cs) => {
      const next = cs.filter((c) => c.key !== key);
      // Removing the last card also un-checks "create accounts for my children".
      if (next.length === 0) setWantsKids(false);
      return next;
    });

  // Tracks the parent account once created so a retry after fixing a child does
  // NOT re-register the parent (Req 4.3); resumes at the child loop instead.
  const parentTokenRef = useRef<string | null>(null);
  // Children already made, by card: a retry after a later child failed must not
  // post them again, or their own name comes back as taken.
  const createdChildrenRef = useRef(new Map<string, { id: string; username: string }>());

  // Persist device trust after a successful auth, when the Trust toggle is on.
  // The access token is already installed (right after /auth/register), so the
  // device endpoint authenticates. Non-fatal — signup completes regardless.
  const persistTrust = async () => {
    if (!trust) return;
    try {
      await device.trust();
    } catch {
      /* non-fatal */
    }
  };

  // Demo → account carryover (demo-project-carryover Req 2, 10). Fired once at
  // signup completion: if the visitor built a demo project, re-home it into this
  // new account so it appears as a building in their world. Non-fatal — any
  // failure (or absent token) must not block signup; the backend records the
  // outcome via demo.carryover_* events. Requires the new account's access token
  // to already be set (done right after /auth/register below).
  const maybeClaimDemoProject = async (childIds: string[] = []) => {
    let token: string | null = null;
    try { token = localStorage.getItem('demo_token'); } catch { token = null; }
    if (!token) return;
    // Explicit ordering guard (Req 2.6): never fire the claim before the new
    // account's access token is installed, or it would be an unauthenticated
    // 401. setAccessToken(...) runs right after /auth/register above; this guard
    // makes the dependency explicit so a refactor can't reorder it.
    if (!getAccessToken()) return;
    try {
      const res = await demo.claim(token, childIds);
      if (res.carried) {
        try { localStorage.removeItem('demo_token'); } catch { /* ignore */ }
      }
    } catch { /* non-fatal — signup still completes */ }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    // Any real child that isn't ready (incomplete, still checking, taken, or
    // colliding) blocks submit and reveals inline errors — cards stay removable,
    // so the user is never stuck. Empty cards are ignored.
    if (isAdult && wantsKids && hasBlockingChild) {
      setTriedSubmit(true);
      return;
    }
    setError('');
    setLoading(true);
    try {
      // Register the parent ONCE. On a retry after fixing a child, the parent
      // already exists — reuse the stored token rather than re-registering.
      let token = parentTokenRef.current;
      if (!token) {
        const resp = await fetch(`${API_BASE}/auth/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...clientHeaders() },
          credentials: credentialsMode(),
          body: JSON.stringify({
            username, password, birthdate,
            ...nativeAuthFields(),
            email: email || undefined,
            birthdate_source: 'exact',
            personal_device: trust,
            // Cross-device intent persistence (R3.2(b)). Backend writes
            // these onto the new user row; consumed on first /auth/login
            // from any device. Read fresh from URL+sessionStorage at
            // submit time so a consent-gate detour or post-mount nav
            // doesn't lose them.
            ...(() => {
              const persisted = readPersistedIntent();
              if (!persisted) return {};
              return {
                intent: persisted.intent,
                intent_source: persisted.source,
              };
            })(),
          }),
        });
        if (!resp.ok) {
          const body = await resp.json().catch(() => ({}));
          const detail = (body as { detail?: string }).detail || '';
          if (detail.toLowerCase().includes('already registered')) setError('That email is already registered. Try logging in.');
          else if (detail.toLowerCase().includes('username')) setError('That username is taken. Try another.');
          else setError(detail || 'Could not create account.');
          return;
        }
        const data = await resp.json();
        adoptNativeRefreshToken(data);
        token = data.access_token ?? null;
        if (token) {
          parentTokenRef.current = token;
          // HttpOnly access cookie is the canonical credential (R15.3);
          // in-memory token covers the Bearer fallback path. We don't
          // mirror to localStorage anymore — XSS would read it.
          setAccessToken(token);
        }

        // launch-ux-followups Item 5: record the adult's Terms
        // acceptance (the kid path captures Terms at the Consent
        // Gate). Best-effort — a transient failure here doesn't
        // unwind the just-completed signup.
        if (token && termsRequired && acceptedTerms) {
          try {
            // Absolute — a bare "/config/public" hits the frontend origin's
            // SPA fallback in prod, so `cfg` parses as nothing and every adult
            // signup silently records terms_version 1 regardless of the real
            // version. Benign only while the version IS 1. See useLifecycle.ts.
            const cfgResp = await fetch(`${API_BASE}/config/public`, { credentials: "same-origin" });
            const cfg = await cfgResp.json();
            const termsVersion = typeof cfg?.terms_version === "number" ? cfg.terms_version : 1;
            await acceptTermsApi(termsVersion);
          } catch {
            /* non-blocking */
          }
        }
      }
      if (!token) { setError('Could not create account.'); return; }

      // R2.3: kid self-registration auto-invokes start_trial_no_card so a new
      // cabined kid lands in `trialing` (with can_create=true) instead of the
      // default `free`/locked state. Adult flows route through CheckoutPanel
      // which handles trial-with-card OR explicit skip-to-free, so they
      // don't need this auto-call. Non-blocking: a transient failure
      // doesn't unwind a just-completed signup — the kid can retry from
      // the billing UI if their next can_create check shows them locked.
      if (!isAdult) {
        try {
          await fetch(`${API_BASE}/billing/start_trial_no_card`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ plan_id: 'single' }),
          });
        } catch {
          /* non-blocking */
        }
      }

      // Create the ready children. A non-OK response is surfaced and STOPS the
      // flow — never silently dropped (Req 4 / Pattern B).
      if (isAdult && wantsKids && readyChildren.length > 0) {
        for (const child of readyChildren) {
          if (createdChildrenRef.current.has(child.key)) continue;
          const post = (bearer: string) => fetch(`${API_BASE}/auth/children`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
            body: JSON.stringify({
              username: child.name.trim(),
              birthdate: child.birthdate,
              password: child.password || undefined,
              // No email at signup — the parent doesn't yet have an
              // account, let alone verifiable parental consent (VPC).
            }),
          });
          let cr: Response;
          try {
            cr = await post(token);
            // The access token from /auth/register lasts 30 minutes and a parent
            // can take longer than that over a card. authFetch never refreshes
            // an /auth/ path, so it happens here.
            if (cr.status === 401 && await refreshAccessToken()) {
              token = getAccessToken() ?? token;
              parentTokenRef.current = token;
              cr = await post(token);
            }
          } catch {
            setError(`Couldn't create ${child.name.trim()} — check your connection and try again.`);
            return;
          }
          if (cr.ok) {
            const cd = await cr.json();
            createdChildrenRef.current.set(child.key, { id: cd.id, username: cd.username });
            continue;
          }
          const body = await cr.json().catch(() => ({}));
          const detail = (body as { detail?: unknown }).detail;
          const reason = typeof detail === 'string' ? detail : '';
          if (cr.status === 409) {
            // A real conflict: re-flag the card, surface it, stop advancing.
            updateChild(child.key, { availability: 'taken' });
            setTriedSubmit(true);
            setError(
              `Couldn't create ${child.name.trim()}: ${reason || 'that name is taken'}. ` +
              'Please fix the highlighted card and try again.',
            );
            observability.emit({
              event_type: 'signup.child_username_rejected',
              user_id: decodeJwtSub(token),
              event_data: { reason: 'taken' },
            });
          } else if (cr.status === 401) {
            setError(`Your account is saved, but we need you to log in again. You can add ${child.name.trim()} after that.`);
          } else if (cr.status === 400 || cr.status === 422) {
            setError(`Couldn't create ${child.name.trim()}: ${reason || 'please check the card'}.`);
          } else {
            setError(`Couldn't create ${child.name.trim()} just now. Your account is saved, so please try again.`);
          }
          return;
        }
        const created = readyChildren.flatMap((c) => createdChildrenRef.current.get(c.key) ?? []);
        setCreatedChildIds(created);
        if (created.length > 0) {
          await persistTrust();
          await maybeClaimDemoProject(created.map((c) => c.id));
          await startVerification(token);
          return;
        }
      }
      await persistTrust();
      await maybeClaimDemoProject();
      setPhase('done');
    } catch {
      setError('Something went wrong. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const startVerification = async (token: string) => {
    try {
      const resp = await fetch(`${API_BASE}/auth/parent-verification/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ callback_url: webOrigin() + '/internal' }),
      });
      if (!resp.ok) { setPhase('done'); return; }
      const data = await resp.json();
      if (data.already_verified) { toPresets(true); }
      else { setVerificationUrl(data.verification_url); setVerificationSessionId(data.session_id); setPhase('verify'); }
    } catch { setPhase('done'); }
  };

  // Apply the chosen preset for the current child, then advance (or finish).
  // The ladder only updates local state now; this commits on Next/Finish so a
  // parent can deliberate without a stray click immediately advancing.
  const handlePresetNext = async () => {
    setError('');
    const child = createdChildIds[presetIndex];
    if (child) {
      // Same bug as VerificationPoller: the old bare fetch sent
      // `Bearer null` (api.ts purges localStorage.access_token) and no
      // cookie, so this 401'd — and `.catch(() => {})` swallowed it. The
      // parent picked a preset, saw no error, and the child silently stayed
      // on the `solo` value auth.py assigns at creation (2026-08-21).
      try {
        await request(`/privacy/child/${child.id}/preset`, {
          method: 'PUT',
          body: JSON.stringify({ preset: kidPreset }),
        });
      } catch (err) {
        // Without the grown-up check the child stays Solo, which is what the
        // note above the ladder says; that is not a failure to stop on.
        const needsCheck = (err as Error)?.message === 'parent_verification_required';
        if (needsCheck) setParentVerified(false);
        // Do not advance past a setting that did not save.
        if (!needsCheck) setError(
          `Couldn't save ${child.username}'s settings. You can change them any time in Parental Controls.`,
        );
        observability.emit({
          event_type: 'signup.child_preset_save_failed',
          user_id: decodeJwtSub(getAccessToken()),
          event_data: {
            child_id: child.id,
            preset: kidPreset,
            reason: err instanceof Error ? err.message : 'unknown',
          },
        }).catch(() => {});
        if (!needsCheck) return;
      }
    }
    if (presetIndex < createdChildIds.length - 1) {
      setPresetIndex(presetIndex + 1);
      setKidPreset(parentVerified ? 'moderated' : 'solo');
    } else {
      setPhase('done');
    }
  };

  // --- Verify phase ---
  if (phase === 'verify') {
    return (
      <div style={pageStyle}><div style={cardStyle}>
        <h2 style={headingStyle}>Quick grown-up check</h2>
        <p style={{ color: ChromeTokens.color.inkMuted, fontSize: 14, margin: 0 }}>
          To set up your kids&apos; accounts, we verify you&apos;re a grown-up with our partner. Just a quick selfie — takes a moment.
        </p>
        {verificationUrl && (
          <iframe src={verificationUrl} style={{ width: '100%', height: 420, border: 'none', borderRadius: ChromeTokens.radius.md, marginTop: 12 }} allow="camera" title="Grown-up verification" />
        )}
        <VerificationPoller sessionId={verificationSessionId} onComplete={() => toPresets(true)} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginTop: 12 }}>
          <button onClick={() => toPresets(false)} style={{ ...linkStyle, display: 'inline', width: 'auto', marginTop: 0 }}>Skip for now</button>
          <InfoTip text="You can skip this — your child can still sign in and create. They just won't be able to connect with friends, see the kid-friendly curated public projects, or make a personalized avatar until a grown-up is verified." />
        </div>
      </div></div>
    );
  }

  // --- Presets phase ---
  if (phase === 'presets') {
    const child = createdChildIds[presetIndex];
    const isLast = presetIndex >= createdChildIds.length - 1;
    return (
      <div style={pageStyle}><div style={cardStyle}>
        <h2 style={{ ...headingStyle, marginBottom: 2 }}>What can {child?.username} do?</h2>
        <p style={{ color: ChromeTokens.color.inkMuted, fontSize: 13, margin: '0 0 14px' }}>{presetIndex + 1} of {createdChildIds.length} · change anytime in Parental Controls</p>
        {!parentVerified && (
          <p data-testid="preset-needs-check" style={{ color: ChromeTokens.color.inkMuted, fontSize: 13, margin: '0 0 12px' }}>
            Until you finish the grown-up check, {child?.username} creates on their own (Solo). After the check,
            choose Moderated or more in Parental Controls.
          </p>
        )}
        <PresetLadder value={kidPreset} onChange={setKidPreset} />
        {error && <p style={{ color: ChromeTokens.color.danger, fontSize: 13, margin: '12px 0 0' }}>{error}</p>}
        <button style={{ ...primaryBtnStyle, marginTop: 16 }} onClick={handlePresetNext}>{isLast ? 'Finish' : 'Next child'}</button>
      </div></div>
    );
  }

  // --- Done phase ---
  if (phase === 'done') {
    return (
      <div style={pageStyle}><div style={{ ...cardStyle, textAlign: 'center' }}>
        <div style={{ fontSize: 36 }}>🎉</div>
        <h2 style={{ ...headingStyle, marginTop: 8 }}>Welcome, {username}!</h2>
        <p style={{ color: ChromeTokens.color.inkMuted, fontSize: 14, margin: '8px 0 16px' }}>Your account is ready.</p>
        {email && <p style={{ fontSize: 13, color: ChromeTokens.color.inkMuted, margin: '0 0 12px' }}>✉️ Verification link sent to <strong>{email}</strong></p>}
        <button onClick={onComplete} style={primaryBtnStyle}>Start building!</button>
      </div></div>
    );
  }

  // --- Main form ---
  return (
    <div style={pageStyle}><div style={cardStyle}>
      {/* IntentRibbon — additive only. Renders null when no intent in
          URL/sessionStorage, leaving the form layout byte-identical to
          today (public-shareable-link spec R3.1 contract). */}
      <IntentRibbon intent={intentState.intent} sourceTitle={intentState.sourceTitle} />
      <img src="/logo/wordmark-color-small.png" alt="world by me" style={{ height: 32, width: 'auto', display: 'block', margin: '0 auto 8px' }} />
      <h2 style={headingStyle}>Create your account</h2>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <NameField value={username} onChange={setUsername} checkAvailability={checkUsernamePublic} placeholder="Pick a username" />

        <div style={{ position: 'relative' }}>
          <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
            placeholder="Password" autoComplete="new-password" style={{ ...inputStyle, paddingRight: 40 }} required />
          {/* 44pt by the tap slop, and named for what it shows (13.11, first-ten-minutes pass). */}
          <button type="button" className="wbm-tap-slop" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? 'Hide password' : 'Show password'}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: ChromeTokens.color.inkMuted }}>
            <Icon name={showPassword ? 'eye-off' : 'eye'} size={18} />
          </button>
        </div>

        <label style={{ fontSize: 13, color: ChromeTokens.color.inkMuted }}>
          Birthday
          <input type="date" value={birthdate} onChange={e => setBirthdate(e.target.value)} style={{ ...inputStyle, display: 'block', marginTop: 4, width: '100%' }} required />
        </label>

        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder={emailRequired ? "Email (required)" : "Email (optional)"}
          autoComplete="email" style={inputStyle} required={emailRequired ?? false} />

        {/* Terms acceptance — required for 13+, informational note for
            under-13 (binding acceptance is captured at the Consent Gate
            when a grown-up agrees on the child's behalf). */}
        {age !== null && termsRequired && (
          <label style={checkStyle}>
            <input
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
            <span>
              I agree to the{" "}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLegalDoc("terms");
                }}
                style={{ background: "none", border: "none", padding: 0, color: ChromeTokens.color.ink, cursor: "pointer", textDecoration: "underline", font: "inherit" }}
              >
                Terms of Service
              </button>
              {" "}and{" "}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLegalDoc("privacy");
                }}
                style={{ background: "none", border: "none", padding: 0, color: ChromeTokens.color.ink, cursor: "pointer", textDecoration: "underline", font: "inherit" }}
              >
                Privacy Policy
              </button>
              .
            </span>
          </label>
        )}
        {age !== null && !termsRequired && (
          <p style={{ fontSize: 12.5, color: ChromeTokens.color.inkMuted, margin: 0, lineHeight: 1.45 }}>
            A grown-up agrees to our Terms &amp; Privacy when they say okay.
          </p>
        )}

        {isAdult && (
          <label style={checkStyle}><input type="checkbox" checked={wantsKids} onChange={e => toggleKids(e.target.checked)} /> I&apos;d also like to create accounts for my children</label>
        )}

        {/* Inline child creation — each card IS a child (no hidden draft).
            Everyone is created together by the button below. */}
        {isAdult && wantsKids && (
          <div style={{ borderTop: `1px solid ${ChromeTokens.color.baseMuted}`, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <strong style={{ fontSize: 14, color: ChromeTokens.color.ink }}>Your children</strong>
              <p style={{ fontSize: 12.5, color: ChromeTokens.color.inkMuted, margin: '3px 0 0', lineHeight: 1.45 }}>
                Each card becomes its own profile. Fill in as many as you need — the button below creates you and everyone here together.
              </p>
            </div>
            {childForms.map((c, i) => (
              <ChildCard
                key={c.key}
                index={i}
                child={c}
                status={statusOf(c)}
                colliding={collisions.has(c.key)}
                triedSubmit={triedSubmit}
                parentUsername={username}
                onChange={(patch) => updateChild(c.key, patch)}
                onAvailabilityChange={(availability) => {
                  // NameField repeats its own last answer on every render, and that
                  // answer predates the server's 409. Editing the name goes through
                  // 'checking', which clears the flag.
                  if (c.availability === 'taken' && availability === 'available') return;
                  // Guard: only write when the status actually changes.
                  // NameField's effect re-fires whenever this callback's
                  // identity changes (every parent render); without this guard
                  // the setState → re-render → new-identity cycle is an infinite
                  // render loop that hangs the page.
                  if (availability !== c.availability) {
                    updateChild(c.key, { availability });
                  }
                }}
                onRemove={() => removeChild(c.key)}
              />
            ))}
            <button type="button" onClick={addChild} style={addChildBtnStyle}>
              <Icon name="user-plus" size={14} /> Add another child
            </button>
          </div>
        )}

        <TrustDeviceToggle
          checked={trust}
          onChange={setTrust}
          subtitle={isAdult && wantsKids && readyChildren.length > 0
            ? 'Stay signed in, and let your kids tap their picture to sign in here.'
            : undefined}
        />

        {/* Live recap — reassures the parent that typed kids are included
            without pressing anything. */}
        {isAdult && wantsKids && realChildren.length > 0 && (
          <div style={recapStyle}>
            <Icon name="sparkles" size={15} stroke={ChromeTokens.color.clay} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Creating <strong>your account</strong>
              {readyChildren.length > 0 && (
                <> plus {readyChildren.map((c, i) => (
                  <span key={c.key}>{i > 0 ? ', ' : ' '}<strong>{c.name.trim()}</strong></span>
                ))}</>
              )}
              {hasBlockingChild && (
                <span style={{ color: ChromeTokens.color.inkMuted }}>
                  {' '}— finish the highlighted card{realChildren.length - readyChildren.length > 1 ? 's' : ''} to include {realChildren.length - readyChildren.length > 1 ? 'them' : 'that one'}.
                </span>
              )}
            </span>
          </div>
        )}

        {error && <p style={{ color: ChromeTokens.color.danger, fontSize: 13, margin: 0 }}>{error}</p>}

        {/* Disabled only by invalid ACCOUNT fields — never by incomplete child
            cards (clicking those reveals their inline errors instead). */}
        <button type="submit" disabled={!canSubmit} style={{ ...primaryBtnStyle, opacity: canSubmit ? 1 : 0.5 }}>
          {loading
            ? 'Creating…'
            : isAdult && wantsKids && readyChildren.length > 0
              ? `Create account & ${readyChildren.length} ${readyChildren.length > 1 ? 'profiles' : 'profile'}`
              : 'Create account'}
        </button>
      </form>

      {onBackToLogin && <button className="wbm-tap-slop" onClick={onBackToLogin} style={linkStyle}>Already have an account? Sign in</button>}

      <LegalModal
        open={legalDoc !== null}
        doc={legalDoc ?? "terms"}
        onClose={() => setLegalDoc(null)}
      />
    </div></div>
  );
}

function FieldError({ text }: { text: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 5, fontSize: 12, fontWeight: 600, color: ChromeTokens.color.danger }}>
      <Icon name="info" size={12} stroke={ChromeTokens.color.danger} /> {text}
    </span>
  );
}

function ChildCard({
  index,
  child,
  status,
  colliding,
  triedSubmit,
  parentUsername,
  onChange,
  onAvailabilityChange,
  onRemove,
}: {
  index: number;
  child: ChildForm;
  status: ChildStatus;
  colliding: boolean;
  triedSubmit: boolean;
  parentUsername: string;
  onChange: (patch: Partial<ChildForm>) => void;
  onAvailabilityChange: (availability: AvailabilityStatus) => void;
  onRemove: () => void;
}) {
  const dobMissing = !child.birthdate;
  const title = child.name.trim() || `Child ${index + 1}`;
  // Reveal inline errors once the parent has attempted submit (or immediately
  // for a live collision, which is unambiguous and not a "did you forget" case).
  const showErrors = triedSubmit || colliding || status === 'taken';
  // Collision message distinguishes "same as you" from "same as another child".
  const collisionMsg =
    child.name.trim().toLowerCase() === parentUsername.trim().toLowerCase()
      ? 'That matches your username — pick a different name for them.'
      : 'Two children have this name — give each a different one.';
  return (
    <div style={{ border: `1.5px solid ${showErrors && status !== 'ready' && status !== 'incomplete' ? ChromeTokens.color.danger : ChromeTokens.color.baseMuted}`, borderRadius: ChromeTokens.radius.md, padding: 14, background: ChromeTokens.color.base, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 26, height: 26, borderRadius: 999, background: ChromeTokens.color.baseMuted, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <Icon name="user" size={14} stroke={ChromeTokens.color.inkMuted} />
        </span>
        <strong style={{ fontSize: 14, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: ChromeTokens.color.ink }}>{title}</strong>
        <ChildStatusBadge status={status} />
        <button type="button" aria-label={`Remove ${title}`} onClick={onRemove}
          style={{ width: 32, height: 32, borderRadius: 999, border: 'none', background: ChromeTokens.color.baseMuted, color: ChromeTokens.color.ink, cursor: 'pointer', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <Icon name="x" size={14} />
        </button>
      </div>
      <div>
        <NameField
          value={child.name}
          onChange={(name) => onChange({ name })}
          checkAvailability={checkUsernamePublic}
          onAvailabilityChange={onAvailabilityChange}
          placeholder="Child's name"
        />
        {/* In-batch collisions aren't caught by the availability check (the
            names aren't persisted yet) — surface them here. */}
        {colliding && <FieldError text={collisionMsg} />}
        {showErrors && !colliding && !child.name.trim() && <FieldError text="Give them a name" />}
      </div>
      <input style={inputStyle} type="password" placeholder="Their secret password (optional)" value={child.password} onChange={(e) => onChange({ password: e.target.value })} />
      <div>
        <input style={inputStyle} type="date" aria-label={`${title}'s birthday`} value={child.birthdate} onChange={(e) => onChange({ birthdate: e.target.value })} />
        {showErrors && dobMissing && <FieldError text="Add a birthday so we pick the right defaults" />}
      </div>
    </div>
  );
}

// Header badge — reflects the FULL card status (Req 3.5): never shows "Ready"
// while the username is still checking, taken, or colliding.
function ChildStatusBadge({ status }: { status: ChildStatus }) {
  if (status === 'ready') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: ChromeTokens.color.grass }}>
        <Icon name="check" size={13} stroke={ChromeTokens.color.grass} /> Ready
      </span>
    );
  }
  if (status === 'checking') {
    return <span style={{ fontSize: 12, fontWeight: 600, color: ChromeTokens.color.inkMuted }}>Checking…</span>;
  }
  if (status === 'taken' || status === 'collision') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 700, color: ChromeTokens.color.danger }}>
        <Icon name="info" size={13} stroke={ChromeTokens.color.danger} /> {status === 'collision' ? 'Duplicate' : 'Name taken'}
      </span>
    );
  }
  return null; // empty / incomplete — no badge
}

/** Consecutive poll failures before we stop hammering the API and give up. */
const VERIFICATION_POLL_MAX_FAILURES = 3;

function VerificationPoller({ sessionId, onComplete }: { sessionId: string; onComplete: () => void }) {
  // `onComplete` is an inline arrow at the call site, so a new identity every
  // parent render. Holding it in a ref keeps it out of the effect deps —
  // otherwise the interval is torn down and recreated on every render and the
  // 3s cadence never actually elapses.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!sessionId) return;
    // Go through `request` (api.ts), NOT a bare fetch with a localStorage
    // token. api.ts PURGES `localStorage.access_token` on load — the access
    // token is an HttpOnly cookie plus an in-memory Bearer since the R15.3
    // cookie migration. A hand-rolled `Bearer ${localStorage.getItem(...)}`
    // sends the literal string "Bearer null", and a bare fetch omits the
    // cookie cross-origin, so every poll was a guaranteed 401. That is what
    // made an APPROVED verification look pending forever (2026-08-21).
    let failures = 0;
    let stopped = false;
    const interval = setInterval(async () => {
      if (stopped) return;
      try {
        const data = await request<VerificationStatusResponse>(
          `/auth/parent-verification/status/${sessionId}`,
        );
        failures = 0;
        if (data.status === 'approved' || data.status === 'declined') {
          stopped = true;
          clearInterval(interval);
          if (data.status === 'approved') onCompleteRef.current();
        }
      } catch {
        // Never poll forever into a wall. A poller that silently retries a
        // hard failure every 3s is indistinguishable from "still pending" to
        // the parent, and buries the real defect in a 401 storm.
        failures += 1;
        if (failures >= VERIFICATION_POLL_MAX_FAILURES) {
          stopped = true;
          clearInterval(interval);
          observability.emit({
            event_type: 'signup.verification_poll_abandoned',
            user_id: decodeJwtSub(getAccessToken()),
            event_data: { session_id: sessionId, failures },
          }).catch(() => {});
        }
      }
    }, 3000);
    return () => { stopped = true; clearInterval(interval); };
  }, [sessionId]);
  return null;
}

const pageStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  minHeight: '100vh', padding: 24,
  background: `linear-gradient(180deg, ${ChromeTokens.color.sky} 0%, ${ChromeTokens.color.base} 100%)`,
};

// Border-box: at 100% plus its padding the card was 56px wider than the
// page's content box, and a phone scrolled sideways (iPhone compact walk).
const cardStyle: React.CSSProperties = {
  width: '100%', maxWidth: 380, padding: 28, borderRadius: ChromeTokens.radius.lg,
  boxSizing: 'border-box',
  background: `color-mix(in srgb, ${ChromeTokens.color.base} 92%, transparent)`,
  boxShadow: ChromeTokens.shadow.lifted,
};

const headingStyle: React.CSSProperties = {
  margin: '0 0 12px', fontSize: 20, fontWeight: 700, color: ChromeTokens.color.ink, textAlign: 'center',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', fontSize: 15, borderRadius: ChromeTokens.radius.md,
  border: `1.5px solid ${ChromeTokens.color.baseMuted}`, background: ChromeTokens.color.base,
  color: ChromeTokens.color.ink, outline: 'none', fontFamily: ChromeTokens.font.ui,
  boxSizing: 'border-box',
};

const checkStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: ChromeTokens.color.ink,
};

const addChildBtnStyle: React.CSSProperties = {
  alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '7px 14px', borderRadius: ChromeTokens.radius.pill,
  border: `1.5px dashed ${ChromeTokens.color.baseMuted}`, background: 'transparent',
  color: ChromeTokens.color.ink, fontSize: 13, fontWeight: 700, cursor: 'pointer',
  fontFamily: ChromeTokens.font.ui,
};

const recapStyle: React.CSSProperties = {
  display: 'flex', gap: 8, alignItems: 'flex-start',
  background: ChromeTokens.color.baseMuted, borderRadius: ChromeTokens.radius.md,
  padding: '10px 12px', fontSize: 13, lineHeight: 1.45, color: ChromeTokens.color.ink,
};

const primaryBtnStyle: React.CSSProperties = {
  width: '100%', padding: '12px 20px', borderRadius: ChromeTokens.radius.pill,
  border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 700,
  background: ChromeTokens.color.sun, color: ChromeTokens.color.ink, fontFamily: ChromeTokens.font.ui,
};

const linkStyle: React.CSSProperties = {
  background: 'none', border: 'none', color: ChromeTokens.color.inkMuted,
  fontSize: 13, cursor: 'pointer', marginTop: 8, textAlign: 'center', display: 'block', width: '100%',
};
